import { type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Play, X } from "lucide-react";
import { JOIN_URL } from "@/lib/club";
import { useMenu, FOLLOW } from "../ConceptBits";
import { CLUB, Draw, EASE, HOME, PAGES, type PageKey } from "./bits";
import "./Hero.css";

/* Hero, nav, and phone menu. Positions are mockup pixels on a 1536 × 1024 stage. */

const STAGE = { w: 1536, h: 1024 };
const box = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  left: `${(x / STAGE.w) * 100}%`, top: `${(y / STAGE.h) * 100}%`,
  ...(w !== undefined && { width: `${(w / STAGE.w) * 100}%` }),
  ...(h !== undefined && { height: `${(h / STAGE.h) * 100}%` }),
});

function Hand({ children, delay, className, style }: { children: ReactNode; delay: number; className: string; style?: CSSProperties }) {
  return <motion.div className={className} style={style} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.45, ease: EASE }}>{children}</motion.div>;
}

const MENU = [
  { to: PAGES.about, label: "About", note: "hi, it’s me!" },
  { to: PAGES.videos, label: "Videos", note: "new every week" },
  { to: CLUB, label: "The club", note: "sew with me live" },
  { to: PAGES.favorites, label: "Favorites", note: "stuff I love" },
  { to: PAGES.retreats, label: "Retreats" },
  { to: PAGES.shop, label: "Shop" },
];

/** Phone menu: the blue page opens from the toggle like a pink-on-blue poster. */
function Menu({ menu }: { menu: ReturnType<typeof useMenu> }) {
  const at = "circle(0% at calc(100% - 38px) 38px)";
  return <AnimatePresence>
    {menu.open && <motion.div id="b2-menu" ref={menu.panel} className="b2-menu" role="dialog" aria-modal="true" aria-label="Menu"
      initial={{ clipPath: at }} animate={{ clipPath: "circle(150% at calc(100% - 38px) 38px)" }} exit={{ clipPath: at }} transition={{ duration: 0.5, ease: EASE }}>
      <div className="b2-menu-top">
        <Link to={HOME} className="b2-logo">A Few Loose Threads</Link>
        <button type="button" className="b2-burger" onClick={() => menu.setOpen(false)} aria-label="Close menu"><X size={24} aria-hidden="true" /></button>
      </div>
      <p className="b2-menu-hand">where to?</p>
      <nav aria-label="Main">
        <ul>{MENU.map((m, i) => <motion.li key={m.to} initial={{ opacity: 0, y: 30, rotate: -6 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.5, ease: EASE }}>
          <Link to={m.to}>{m.label}</Link>{m.note && <span className="b2-note">{m.note}</span>}
        </motion.li>)}</ul>
      </nav>
      <motion.div className="b2-menu-foot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <Link to={CLUB} className="b2-btn b2-btn-yellow">Sew with me <ArrowRight size={18} aria-hidden="true" /></Link>
        <ul className="b2-foot-social">{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
      </motion.div>
      <svg className="b2-menu-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d="M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z" delay={0.6} width={3.4} /></svg>
    </motion.div>}
  </AnimatePresence>;
}

const NAV: { key: PageKey; to: string; label: string }[] = [
  { key: "about", to: PAGES.about, label: "About" },
  { key: "videos", to: PAGES.videos, label: "Videos" },
  { key: "club", to: CLUB, label: "The Club" },
  { key: "favorites", to: PAGES.favorites, label: "Favorites" },
  { key: "retreats", to: PAGES.retreats, label: "Retreats" },
  { key: "shop", to: PAGES.shop, label: "Shop" },
];

/** A page's own header button, in place of the default "Join the club" / "Sew with me". */
export type NavCta = { label: ReactNode; to: string; external?: boolean; arrow?: boolean; ariaLabel?: string };

/** `current` marks the page's own link; on the club page the button goes straight to checkout.
    `cta` replaces that button (the members' prototype uses it so it never links to real checkout). */
export function Nav({ club = false, current, cta }: { club?: boolean; current?: PageKey; cta?: NavCta }) {
  const menu = useMenu();
  const here = club ? "club" : current;
  return <header className="b2-nav">
    <Link to={HOME} className="b2-logo">A Few Loose Threads</Link>
    <nav aria-label="Main">
      {NAV.map(n => <Link key={n.key} to={n.to} aria-current={here === n.key ? "page" : undefined}>{n.label}</Link>)}
    </nav>
    {cta
      ? (cta.external
        ? <a href={cta.to} className="b2-cta" aria-label={cta.ariaLabel}>{cta.label}{cta.arrow !== false && <ArrowRight size={18} aria-hidden="true" />}</a>
        : <Link to={cta.to} className="b2-cta" aria-label={cta.ariaLabel}>{cta.label}{cta.arrow !== false && <ArrowRight size={18} aria-hidden="true" />}</Link>)
      : here === "club"
      ? <a href={JOIN_URL} className="b2-cta">Join the club <ArrowRight size={18} aria-hidden="true" /></a>
      : <Link to={CLUB} className="b2-cta">Sew with me <ArrowRight size={18} aria-hidden="true" /></Link>}
    <button ref={menu.toggle} type="button" className="b2-burger" aria-label="Open menu" aria-expanded={menu.open} aria-controls="b2-menu" onClick={() => menu.setOpen(true)}>
      <svg viewBox="0 0 28 20" aria-hidden="true"><path d="M2 3 C 10 1, 18 4, 26 2" /><path d="M2 10 C 9 11, 19 9, 26 10" /><path d="M2 17 C 10 18, 17 16, 26 18" /></svg>
    </button>
    <Menu menu={menu} />
  </header>;
}

const POLAROIDS = [
  { src: "/chelsea-and-cosmo.jpg", pos: "62% 72%", zoom: 2.1, caption: <>Chief quality<br />inspector <small>(a.k.a. Cosmo)</small></>, alt: "Cosmo the cat", at: [1030, 680, 150, 236], r: -10, tape: null },
  { src: "/img/products/qs-mat.jpg", pos: "50% 30%", zoom: 1.5, caption: <>Fabric makes<br />me happy.</>, alt: "A tall stack of colorful folded fabric", at: [1180, 668, 168, 238], r: 4, tape: "pink" },
  { src: "/chelsea-rainbow-portrait.jpg", pos: "58% 26%", zoom: 1.6, caption: <>Same me.<br />More sewing.<br />Let’s go.</>, alt: "Chelsea smiling in her straw hat", at: [1312, 660, 196, 290], r: 6, tape: "green" },
] as const;

export function Hero() {
  return <section className="b2-hero" aria-labelledby="b2-title">
    <div className="b2-stage">
      <Nav />

      <h1 id="b2-title" className="b2-title">
        <span className="b2-hi" style={box(136, 108)}>Hi, I’m</span>
        <span className="b2-name" style={box(48, 112)} aria-label="Chelsea">
          {"chelsea".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <svg className="b2-abs b2-yellow" style={box(136, 166, 170, 30)} viewBox="0 0 170 30" aria-hidden="true"><Draw d="M4 22 C 50 12, 110 10, 166 8" delay={0.7} width={5} /></svg>

      <motion.img className="b2-abs b2-chelsea" src="/chelsea-cutout-trim.webp" alt="Chelsea smiling with one arm raised, in a straw hat and a colorful checked sundress"
        style={box(548, 76, 420, 1150)} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7, ease: EASE }} />

      <Hand className="b2-abs b2-script b2-make" style={box(104, 512)} delay={0.9}>I make colorful<br />things (and I’d love<br />for you to make<br />them too).</Hand>
      <svg className="b2-abs b2-yellow" style={box(398, 536, 42, 62)} viewBox="0 0 42 62" aria-hidden="true">
        <Draw d="M6 18 L32 4" delay={1.3} width={4.5} /><Draw d="M10 34 L38 30" delay={1.38} width={4.5} /><Draw d="M8 50 L30 56" delay={1.46} width={4.5} />
      </svg>

      <Hand className="b2-abs b2-copy" style={box(132, 690, 330)} delay={1.0}>
        <p>Sewing, creativity, and a community of good people. Welcome to my corner of the internet.</p>
        <div className="b2-actions">
          <Link to={PAGES.about} className="b2-btn b2-btn-yellow">Get to know me <ArrowRight size={16} aria-hidden="true" /></Link>
          <a href="#making" className="b2-btn b2-btn-ghost"><Play size={14} fill="currentColor" aria-hidden="true" /> Watch my latest</a>
        </div>
      </Hand>

      <Hand className="b2-abs b2-marker b2-same" style={box(1132, 150)} delay={1.2}>New month.<br /><span>New project.</span></Hand>
      <svg className="b2-abs b2-yellow" style={box(1088, 172, 30, 46)} viewBox="0 0 30 46" aria-hidden="true"><Draw d="M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z" delay={1.45} width={3.6} /></svg>

      <ul className="b2-abs b2-checks" style={box(1052, 506)} aria-label="What you’ll find here">
        {["Sewing", "Good vibes", "Cool people", "Big ideas"].map((t, i) => <li key={t}>
          <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" /><Draw d="M7 13 L11.5 17.5 L19.5 8" delay={1.5 + i * 0.22} width={2.8} /></svg>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.45 + i * 0.22 }}>{t}</motion.span>
        </li>)}
      </ul>

      <Link to={CLUB} className="b2-abs b2-youin" style={box(1338, 508, 136, 104)} aria-label="You in? Join the club">
        <svg viewBox="0 0 136 104" aria-hidden="true"><Draw d="M96 12 C 60 0, 14 14, 8 48 C 2 82, 52 100, 96 90 C 132 80, 136 40, 112 20 C 100 10, 80 8, 64 12" delay={2.3} width={3.4} /></svg>
        <motion.span initial={{ opacity: 0, rotate: -18 }} animate={{ opacity: 1, rotate: -14 }} transition={{ delay: 2.1 }}>You<br />in? ♡</motion.span>
      </Link>

      {POLAROIDS.map((p, i) => <motion.figure key={p.src} className="b2-abs b2-polaroid" style={{ ...box(p.at[0], p.at[1], p.at[2], p.at[3]) }}
        initial={{ opacity: 0, y: 40, rotate: p.r + 12 }} animate={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -6, rotate: p.r / 2, zIndex: 9 }}
        transition={{ delay: 0.9 + i * 0.15, type: "spring", stiffness: 140, damping: 15 }}>
        {p.tape && <i className={`b2-tape b2-tape-${p.tape}`} aria-hidden="true" />}
        <span className="b2-polaroid-img"><img src={p.src} alt={p.alt} style={{ objectPosition: p.pos, transform: `scale(${p.zoom})`, transformOrigin: p.pos }} /></span>
        <figcaption>{p.caption} <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>)}

      <div className="b2-desk" aria-hidden="true" />
      <img className="b2-abs b2-laptop" src="/concept/laptop.png" alt="" style={box(0, 814, 292, 210)} />
      <img className="b2-abs b2-mug" src="/concept/mug.png" alt="" style={box(380, 860, 96, 113)} />
    </div>
  </section>;
}
